import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Modale d'authentification réutilisable (inscription / connexion / Google).
export default function AuthModal({ open, onClose, defaultMode = "register", title, subtitle, redirectPath = "/app", onSuccess }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const google = () => {
    const redirectUrl = window.location.origin + redirectPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await api.post("/auth/forgot-password", { email: form.email });
        setMsg(res.data?.message || "Si un compte existe, un email vient de partir.");
        return;
      }
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register"
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const res = await api.post(path, payload);
      const ref = localStorage.getItem("ref_code");
      if (ref) { try { await api.post("/referral/attach", { code: ref }); } catch {} localStorage.removeItem("ref_code"); }
      setUser(res.data);
      onSuccess?.(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Une erreur est survenue. Réessaie.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-obsidian/50 backdrop-blur-sm" onClick={() => !busy && onClose?.()} />
      <div className="relative w-full max-w-md rounded-[24px] bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.5)] border border-border/60 p-7 sm:p-8 fade-up">
        <button data-testid="auth-close" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full text-obsidian/40 hover:text-obsidian hover:bg-obsidian/5 transition-colors"><X size={18} /></button>

        <h2 className="font-display text-2xl font-light tracking-tight">
          {title || (mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Mot de passe oublié" : "Se connecter")}
        </h2>
        <p className="mt-1.5 text-sm text-obsidian/55">
          {subtitle || (mode === "register" ? "Sauvegarde ton bilan et débloque ta préparation." : mode === "forgot" ? "Entre ton email : on t'envoie un lien pour choisir un nouveau mot de passe." : "Content de te revoir.")}
        </p>

        {mode !== "forgot" && (
        <>
        <button data-testid="auth-google-button" onClick={google}
          className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-white px-6 py-3 text-sm font-medium hover:bg-obsidian/[0.03] transition-colors">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continuer avec Google
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-obsidian/40">
          <span className="h-px flex-1 bg-border" /> ou avec ton email <span className="h-px flex-1 bg-border" />
        </div>
        </>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input data-testid="auth-name-input" type="text" placeholder="Prénom" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
          )}
          <input data-testid="auth-email-input" type="email" required placeholder="Adresse email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
          {mode !== "forgot" && (
          <input data-testid="auth-password-input" type="password" required placeholder="Mot de passe (min. 6 caractères)" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
          )}

          {mode !== "register" && mode !== "forgot" && (
            <div className="text-right">
              <button type="button" data-testid="auth-forgot-link" onClick={() => { setErr(""); setMsg(""); setMode("forgot"); }}
                className="text-xs text-obsidian/50 hover:text-sage hover:underline">Mot de passe oublié ?</button>
            </div>
          )}

          {err && <p data-testid="auth-error" className="text-sm text-destructive">{err}</p>}
          {msg && <p data-testid="auth-message" className="text-sm text-sage">{msg}</p>}

          <button data-testid="auth-submit-button" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:bg-obsidian/90 transition-colors disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Envoyer le lien" : "Se connecter"}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-obsidian/55">
          {mode === "forgot" ? (
            <button data-testid="auth-back-login" onClick={() => { setErr(""); setMsg(""); setMode("login"); }}
              className="text-sage font-medium hover:underline">Retour à la connexion</button>
          ) : (<>
            {mode === "register" ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
            <button data-testid="auth-toggle-mode" onClick={() => { setErr(""); setMsg(""); setMode(mode === "register" ? "login" : "register"); }}
              className="text-sage font-medium hover:underline">
              {mode === "register" ? "Se connecter" : "Créer un compte"}
            </button>
          </>)}
        </p>
      </div>
    </div>
  );
}
